/**
 * Rivet's palette restated as plain constants, so a cell renderer never has to
 * hook the theme. Same approach as `src/features/coordinate-frames/theme/
 * tokens.ts`: a `renderCell` runs once per visible cell per scroll frame, and
 * `useTheme()` in that position is a context read the grid does not need.
 *
 * Nothing here imports from `@mui/*` — not even types. The grid is licensed
 * through `@mapped/rivet/dist/mui/*` (a side effect in rivet's data-grid
 * entry), so a direct `@mui/*` import anywhere in this feature silently
 * downgrades the Pro grid. Keeping the token module dependency-free means the
 * whole feature can import it without inheriting that risk.
 */

/** Rivet's ink ramp. Comments name the theme key each value is taken from. */
export const INK = {
  primary: '#191F2C', // text.primary
  muted: '#5E626B', // text.secondary / slate.a70
  faint: '#8C8F95', // text.disabled  / slate.a50
  line: '#D1D2D5', // text.a20 — dashed absence only
  hairline: '#E8E9EA', // divider / text.a10
  paper: '#F9F9F9', // background.paper / text.a03
  sunk: '#f6f6f6', // action.hover / text.a05
  ground: '#FFFFFF',
  action: '#4A3BCF', // primary.main
  actionSoft: '#F6F5Fd', // primary.a10
  actionMid: '#DBD8F5', // primary.a20
} as const

export type InkKey = keyof typeof INK

/**
 * The selected row's wash — OPAQUE, and it has to stay that way.
 *
 * Pinned cells take `background-color: inherit` so the row's tint carries
 * across them, which means the row's colour is also the only thing stopping
 * horizontally-scrolled cells showing THROUGH the pinned columns. A translucent
 * value composites correctly against the page and not at all against the cells
 * sliding underneath, so the tint has to be pre-composited.
 *
 * This is `rgba(74, 59, 207, 0.06)` flattened onto `INK.ground`.
 */
export const SELECTED_BG = '#F4F3FC'
export const SELECTED_BAR = 'inset 2px 0 0 0 #4A3BCF'

/**
 * Connector lifecycle tone. Keys are `ConnectorState` enum members; red is
 * reserved for `ERROR_GENERAL` and appears nowhere else in this file.
 */
export const STATE_TONE = {
  ACTIVE: '#51C0AC',
  CONNECTING: '#F2C94C',
  CONFIGURING: '#F2C94C',
  STOPPING: '#F2C94C',
  STOPPED: '#8C8F95',
  DELETING: '#8C8F95',
  DELETED: '#8C8F95',
  ERROR_GENERAL: '#F56565',
  CONNECTOR_STATE_UNSPECIFIED: '#B4B8C0',
} as const

export type StateToneKey = keyof typeof STATE_TONE

/** warning.main — provenance disagreement, nothing else. */
export const CONFLICT = '#F2C94C'

/** primary.main — the contribution with `selected: true`. */
export const WON = '#4A3BCF'

/**
 * MUST end in `!important`. Rivet's `MuiCssBaseline` injects
 * `* { font-family: NeueHansKendrick, … !important }` (rivet
 * `theme/index.js:72-103`); a universal selector carrying `!important` beats
 * every emotion `sx` rule, so a monospace stack without it silently does
 * nothing and mapping keys render in the sans face.
 */
export const MONO =
  'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace !important'

/**
 * The only categorical colour in the product. It appears in the source avatar,
 * the avatar surplus tile, the provenance strip, the blame gutter and source
 * rows — nowhere else.
 *
 * Error red `#F56565` is deliberately absent: a source is never a failure, and
 * red next to a connector logo would read as "this connector is broken".
 */
export const SOURCE_HUES = [
  '#4A3BCF',
  '#51C0AC',
  '#4D5F80',
  '#F2C94C',
  '#8076DD',
  '#A0AEC0',
  '#A49DE7',
  '#717F99',
] as const

export type SourceHue = (typeof SOURCE_HUES)[number]

/** Avatar edge lengths by position. Tiles are square; radius is 3. */
export const SOURCE_AVATAR = {
  cell: 20,
  header: 16,
  panel: 14,
  gutter: 14,
} as const

export type SourceAvatarSlot = keyof typeof SOURCE_AVATAR

/**
 * Why every source avatar must carry a hue at all:
 *
 * Connector logos DO NOT identify a connector. In the dev org, five connectors
 * serve three distinct marks — `coordinates-demo-source` and
 * `coordinate-fusion-source` return byte-identical images, as do
 * `azure-event-hubs` and `azure-eventhub-destination-v2`. Two connectors on one
 * row would therefore render two identical tiles and read as a rendering bug.
 * The hue, not the logo, is what makes a source distinguishable at a glance;
 * the connector's NAME, not its mark, is what identifies it on hover.
 */

/**
 * The canonical assignment: hue by index over the org's id-sorted connector
 * list, per §9.1. Sorting by id (not by name, which is user-editable and can
 * collide) makes a connector's colour identical in every row, every tab and
 * every session for as long as the org's connector set is unchanged.
 *
 * `ConnectorLookupProvider` builds this once per org from the lookup query.
 * Duplicate ids collapse; the input is not mutated.
 */
export function assignSourceHues(
  connectorIds: readonly string[]
): Map<string, SourceHue> {
  const unique = Array.from(new Set(connectorIds.filter(Boolean))).sort()
  const hues = new Map<string, SourceHue>()
  unique.forEach((id, index) => {
    hues.set(id, SOURCE_HUES[index % SOURCE_HUES.length])
  })
  return hues
}

/**
 * FNV-1a, 32-bit. Chosen because it is four lines, allocation-free and stable
 * forever — the hue must not move between reloads, and a hash that depends on
 * anything but the id string (insertion order, `Math.random`, a build id) would
 * repaint the strip on every mount.
 */
function hashId(id: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i)
    // hash * 16777619, kept in 32-bit range without overflowing to a double.
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash >>> 0
}

/**
 * Hue for a single connector id, with no list to index into.
 *
 * `assignSourceHues` is preferred wherever the org's connector list is in hand,
 * because index-over-sorted-list guarantees no collision below nine connectors.
 * This is the fallback for the ids that arrive without it: a
 * `connectedDataSourceId` on a row whose connector is not in the lookup yet, or
 * a contributor id discovered by edge enrichment. It is deterministic across
 * tabs and reloads, which is the property the avatar actually needs.
 */
export function sourceHue(connectorId: string | null | undefined): SourceHue {
  if (!connectorId) return SOURCE_HUES[0]
  return SOURCE_HUES[hashId(connectorId) % SOURCE_HUES.length]
}
